import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import type { RootState } from '@/store';
import {
  getDashboardMetric2,
  type DashboardMetric2CycleControlCount,
} from '@/services/dashboardService';

const { Text } = Typography;

const DonutWrap = styled.div`
  .export-only {
    display: none;
  }
`;

// Palette for cycles (stable rotation)
const CYCLE_COLORS = [
  '#5BB6F0',
  '#00A37A',
  '#E6A700',
  '#8B5CF6',
  '#EF4444',
  '#F97316',
  '#14B8A6',
  '#64748B',
];

type DonutDatum = {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  controlCount: number;
  percent: number; // 0..100
};

export type EX1ControlDistributionByCycleDonutChartProps = {
  height?: number;
  title?: string;
};

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const Metric2Tooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;

  const d: DonutDatum | undefined = payload?.[0]?.payload;
  if (!d) return null;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 220,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {d.cycleCode + ' ' + d.cycleName}
      </div>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
      >
        <span>Active controls</span>
        <span style={{ fontWeight: 600 }}>{d.controlCount}</span>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          marginTop: 4,
        }}
      >
        <span>Percent</span>
        <span style={{ fontWeight: 600 }}>{formatPercent(d.percent)}</span>
      </div>
      <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.6)' }}>
        Click slice to open Exhibit 5
      </div>
    </div>
  );
};

const renderCenterLabel =
  (total: number) =>
  ({ viewBox }: any) => {
    const { cx, cy } = viewBox || {};
    if (cx == null || cy == null) return null;

    return (
      <>
        <text
          x={cx}
          y={cy}
          textAnchor='middle'
          dominantBaseline='middle'
          fontSize={22}
          fontWeight={700}
        >
          {total}
        </text>
        <text
          x={cx}
          y={cy + 18}
          textAnchor='middle'
          dominantBaseline='middle'
          fontSize={13}
        >
          Active Controls
        </text>
      </>
    );
  };

const ExportTooltipPanel = ({ items }: { items: DonutDatum[] }) => {
  // Show top items (avoid massive PDF); adjust as you like
  const top = items.slice(0, 12);

  return (
    <div
      className='export-only'
      style={{
        marginTop: 12,
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: 12,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        Details (Active Controls by Cycle)
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {top.map((d, idx) => (
          <div
            key={d.cycleId}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              fontSize: 12,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: CYCLE_COLORS[idx % CYCLE_COLORS.length],
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {d.cycleCode} {d.cycleName}
              </span>
            </div>

            <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
              <span style={{ fontWeight: 600 }}>{d.controlCount}</span>
              <span style={{ color: 'rgba(0,0,0,0.65)' }}>
                {formatPercent(d.percent)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {items.length > top.length ? (
        <div style={{ marginTop: 8, fontSize: 12, color: 'rgba(0,0,0,0.6)' }}>
          +{items.length - top.length} more cycles not shown
        </div>
      ) : null}
    </div>
  );
};

export const EX1ControlDistributionByCycleDonutChart: React.FC<
  EX1ControlDistributionByCycleDonutChartProps
> = ({ height = 360, title = 'Control Distribution by Cycle' }) => {
  const navigate = useNavigate();

  // Get annualReportId from Redux
  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [raw, setRaw] = useState<DashboardMetric2CycleControlCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRaw([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric2(annualReportId);
        if (!isMounted) return;
        setRaw(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRaw([]);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 2 (Controls count breakdown by cycle).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const totalActiveControls = useMemo(() => {
    return raw.reduce((sum, x) => sum + (x.controlCount || 0), 0);
  }, [raw]);

  const data: DonutDatum[] = useMemo(() => {
    const total = totalActiveControls || 0;
    return (
      raw
        .filter(x => (x.controlCount ?? 0) > 0)
        .map(x => ({
          cycleId: x.cycleId,
          cycleName: x.cycleName,
          cycleCode: x.cycleCode,
          controlCount: x.controlCount,
          percent: total > 0 ? (x.controlCount / total) * 100 : 0,
        }))
        // Optional: sort descending by count to match typical dashboard expectations
        //.sort((a, b) => b.controlCount - a.controlCount)
    );
  }, [raw, totalActiveControls]);

  const ROW_H = 22; // each tooltip row height (tune)
  const PANEL_HEADER_H = 44; // panel title + padding (tune)
  const PANEL_PADDING = 24; // extra padding/borders (tune)
  const PANEL_MAX = 340; // avoid monster cards (tune)

  const exportPanelRows = Math.min(data.length, 12); // must match ExportTooltipPanel top slice
  const exportPanelHeight = Math.min(
    PANEL_MAX,
    PANEL_HEADER_H + PANEL_PADDING + exportPanelRows * ROW_H,
  );

  // how tall the whole component should be when exporting
  const exportHeight = height + exportPanelHeight;

  const isEmpty = !loading && !errorMessage && data.length === 0;

  // Interaction: click slice -> Exhibit 5 filtered by cycle + active controls
  // Update this route/query to match your actual Exhibit 5 URL contract.
  const goToExhibit5ForCycleActiveControls = (d: DonutDatum) => {
    navigate('/control-activities', {
      state: { cycleId: d.cycleId, cycleCode: d.cycleCode, cycleName: d.cycleName },
    });
  };

  return (
    <Card title={title}>
      <DonutWrap>
        <div
          className='metric2-donut'
          style={{
            height, // web stays the same
          }}
          data-export-height={exportHeight}
        >
          {loading ? (
            <div
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Spin />
            </div>
          ) : errorMessage ? (
            <Alert
              type='error'
              message='Dashboard Metric 2 Error'
              description={errorMessage}
              showIcon
            />
          ) : isEmpty ? (
            <Alert
              type='info'
              message='No data'
              description='No active controls were returned for this report.'
              showIcon
            />
          ) : (
            <>
              {/* chart area */}
              <div style={{ height }}>
                <ResponsiveContainer width='100%' height='100%'>
                  <PieChart
                    margin={
                      isNarrow
                        ? { top: 8, right: 8, bottom: 24, left: 8 }
                        : { top: 8, right: 140, bottom: 8, left: 8 }
                    }
                  >
                    <Tooltip content={<Metric2Tooltip />} />

                    <Legend
                      layout={isNarrow ? 'horizontal' : 'vertical'}
                      align={isNarrow ? 'center' : 'right'}
                      verticalAlign={isNarrow ? 'bottom' : 'middle'}
                      wrapperStyle={
                        isNarrow ? { paddingTop: 12 } : { paddingLeft: 16 }
                      }
                      formatter={(value: any, entry: any) => {
                        const p: DonutDatum | undefined = entry?.payload;
                        if (!p) return value;
                        return `${p.cycleCode + ' ' + p.cycleName} — ${formatPercent(p.percent)}`;
                      }}
                    />

                    <Pie
                      data={data}
                      dataKey='controlCount'
                      nameKey='cycleName'
                      cx={isNarrow ? '50%' : '40%'} // shifts donut left to make room for right legend
                      cy='50%'
                      innerRadius={70}
                      outerRadius={110}
                      paddingAngle={1}
                      isAnimationActive={false}
                      onClick={(_, idx) => {
                        const d = data[idx];
                        if (d?.cycleId)
                          goToExhibit5ForCycleActiveControls(d);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {data.map((_, idx) => (
                        <Cell
                          key={`cell-${idx}`}
                          fill={CYCLE_COLORS[idx % CYCLE_COLORS.length]}
                        />
                      ))}

                      {/* Center label (total active controls) */}
                      <Label
                        position='center'
                        content={renderCenterLabel(totalActiveControls)}
                      />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* export-only panel area */}
              <div
                className='export-only'
                style={{ height: exportPanelHeight }}
              >
                <ExportTooltipPanel items={data} />
              </div>
            </>
          )}
        </div>

        {/* Optional: small note in card footer area */}
        {!loading && !errorMessage ? (
          <div style={{ marginTop: 8, textAlign: 'center' }}>
            <Text type='secondary'>Effective Controls by Process.</Text>
          </div>
        ) : null}
      </DonutWrap>
    </Card>
  );
};

export default EX1ControlDistributionByCycleDonutChart;
